import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PageHero, Reveal, ArrowButton, EASE } from "@/components/bits";
import ProjectCard from "@/components/ProjectCard";
import { PROJECTS, CATEGORIES } from "@/data/content";

export default function OurWork() {
  const [filter, setFilter] = useState("all");
  const visible = useMemo(
    () => (filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter)),
    [filter]
  );

  return (
    <main data-testid="our-work-page">
      <PageHero
        testid="our-work-hero"
        eyebrow="Our Work"
        title={
          <>
            A portfolio of nutrition, healthcare and <span className="text-tnn-purple">technology innovation.</span>
          </>
        }
        lead="Through TNN Africa and our partnerships with founders, healthcare organisations and technology companies, we have contributed to the development of products, programmes and ventures across nutrition and healthcare."
      />

      <section className="bg-tnn-warm pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-wrap gap-2.5" data-testid="portfolio-filters" role="tablist">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={filter === c.key}
                  data-testid={`filter-${c.key}`}
                  onClick={() => setFilter(c.key)}
                  className={`text-xs font-semibold px-5 py-2.5 rounded-full border transition-colors duration-200 ${
                    filter === c.key
                      ? "bg-tnn-purple border-tnn-purple text-white"
                      : "border-tnn-ink/15 text-tnn-muted hover:border-tnn-purple hover:text-tnn-purple bg-white"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence mode="popLayout">
              {visible.map((p, i) => (
                <motion.div
                  key={p.slug}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.4, delay: i * 0.03, ease: EASE }}
                >
                  <ProjectCard project={p} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          <Reveal className="mt-12">
            <p className="text-xs text-tnn-muted/80 max-w-xl leading-relaxed">
              Some of this work is ours, some we built alongside partners, and some is education. We don't own every
              project listed here.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-tnn-warm pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="bg-tnn-deep rounded-[2rem] px-8 py-12 lg:p-16 flex flex-col sm:flex-row sm:items-center justify-between gap-8">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white leading-[1.2] max-w-md">
                Working on something that belongs in this portfolio?
              </h2>
              <ArrowButton to="/contact" variant="white" testid="our-work-cta">Partner With TNN Africa</ArrowButton>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
